import React, { useEffect, useState, useMemo } from 'react';

/**
 * COMPONENTE: SOCCER OVERLAY (VISUAL CHILD)
 * ------------------------------------------------------------------
 * Renderiza el banner de Asistencia con animaciones de partículas GPU.
 * ------------------------------------------------------------------
 */
const Soccer = ({ config }) => {
    const {
        titleText,
        numberText,
        thanksText,
        visible,
        animationEffect,
        titleColor,
        numberGradientStart,
        numberGradientMiddle,
        numberGradientEnd,
        thanksColor,
        particlesColor,
        glowColor,
        backgroundGradientStart,
        backgroundGradientEnd,
        scale,
        titleSize,
        numberSize,
        thanksSize,
        offsetX,
        offsetY,
        logoDataUrl,
        logoScale,
        logoOffsetX,
        logoOffsetY,
        showLogoSweep,
        enableAnimation
    } = config;
    const [phase, setPhase] = useState('idle'); // 'idle' | 'scatter' | 'form' | 'disperse' | 'done'
    const climaxTrigger = config.climaxTrigger;

    // Control del timeline de la animación estelar de introducción (Sin la fase implode)
    useEffect(() => {
        if (visible) {
            if (enableAnimation === false) {
                setPhase('done');
                return;
            }
            setPhase('scatter');
            const t1 = setTimeout(() => setPhase('form'), 1800); // va directo de scatter a form
            const t2 = setTimeout(() => setPhase('disperse'), 4600);
            const t3 = setTimeout(() => setPhase('done'), 5800);
            return () => {
                clearTimeout(t1);
                clearTimeout(t2);
                clearTimeout(t3);
            };
        } else {
            setPhase('idle');
        }
    }, [visible, enableAnimation]);

    // Escuchar disparador manual remoto desde el panel
    useEffect(() => {
        if (visible && climaxTrigger && phase !== 'done' && phase !== 'disperse') {
            setPhase('disperse');
        }
    }, [climaxTrigger, visible]);

    // Escuchar barra espaciadora localmente en el overlay para detonar el clímax
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.code === 'Space') {
                e.preventDefault();
                setPhase('disperse');
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [phase, visible]);

    // Extraer offsets numéricos con auto-escalado a prueba de fallos para alinear las coordenadas
    const rawOffsetY = parseFloat(offsetY) || -12;
    const numericOffsetY = Math.abs(rawOffsetY) > 50 ? rawOffsetY / 10 : rawOffsetY;

    const rawOffsetX = parseFloat(offsetX) || 0;
    const numericOffsetX = Math.abs(rawOffsetX) > 50 ? rawOffsetX / 10 : rawOffsetX;

    // SISTEMA DE ANÁLISIS DE TIPOGRAFÍA DINÁMICO EN SEGUNDO PLANO (Cálculo en memoria, sin canvas visual)
    const targets = useMemo(() => {
        if (!numberText) return [];

        try {
            const off = document.createElement('canvas');
            const width = window.innerWidth || 1920;
            const height = window.innerHeight || 1080;
            off.width = width;
            off.height = height;

            const oCtx = off.getContext('2d', { willReadFrequently: true });
            if (!oCtx) return [];

            // Simular el tamaño de la fuente Cinzel en px basado en la escala vw
            const sizeVw = numberSize || 14.0;
            const fontSizePx = width * (sizeVw / 100);
            oCtx.font = `600 ${fontSizePx}px "Cinzel", serif`;
            oCtx.textAlign = 'center';
            oCtx.textBaseline = 'middle';
            oCtx.fillStyle = 'white';

            // Calcular el centro geométrico aplicando el desplazamiento en vh/px
            const centerY = (height * 0.5) + (height * (numericOffsetY / 100));
            const centerX = (width * 0.5) + numericOffsetX;

            oCtx.fillText(String(numberText), centerX, centerY);

            const imageData = oCtx.getImageData(0, 0, width, height).data;
            const points = [];

            // Muestrear los píxeles opacos del canvas temporal en memoria
            const step = 4;
            for (let y = 0; y < height; y += step) {
                for (let x = 0; x < width; x += step) {
                    const index = (y * width + x) * 4;
                    const alpha = imageData[index + 3];
                    if (alpha > 120) {
                        points.push({
                            x: (x / width) * 100, // Almacenar en porcentaje (%) para responsividad
                            y: (y / height) * 100
                        });
                    }
                }
            }

            // Mezclar aleatoriamente para evitar patrones lineales de acomodo
            return points.sort(() => Math.random() - 0.5);
        } catch (e) {
            console.error('Error generating font targets in background:', e);
            return [];
        }
    }, [numberText, numberSize, numericOffsetX, numericOffsetY]);

    // Inicializar 1200 partículas en el contenedor de pantalla completa para abarcar todo el viewport
    const introParticles = useMemo(() => {
        const list = [];
        for (let i = 0; i < 1200; i++) {
            list.push({
                id: i,
                scatterLeft: Math.random() * 100, // 0% a 100% de la pantalla
                scatterTop: Math.random() * 100,  // 0% a 100% de la pantalla
                delay: Math.random() * 0.6,
                twinkleX: `${(Math.random() - 0.5) * 35}px`,
                twinkleY: `${(Math.random() - 0.5) * 35}px`,
                twinkleDuration: `${Math.random() * 3 + 2}s`,
                color: Math.random() > 0.4 ? '#ffffff' : '#d4af37',
                size: Math.random() * 3 + 1.8 // 1.8px a 4.8px
            });
        }
        return list;
    }, []);

    // Partículas continuas de fondo (polvo dorado)
    const goldenParticles = useMemo(() => {
        const list = [];
        for (let i = 0; i < 20; i++) {
            list.push({
                id: i,
                left: `${Math.random() * 95 + 2.5}%`,
                bottom: `${Math.random() * 20}%`,
                size: `${Math.random() * 5 + 3}px`,
                delay: `${Math.random() * 8}s`,
                duration: `${Math.random() * 8 + 12}s`
            });
        }
        return list;
    }, []);

    const hexToRgba = (hex, alpha = 0.4) => {
        if (!hex) return `rgba(212, 175, 55, ${alpha})`;
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
        return result 
            ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})`
            : `rgba(212, 175, 55, ${alpha})`;
    };

    const numGlow = hexToRgba(glowColor, 0.5);
    const numGlowStrong = hexToRgba(glowColor, 0.9);

    const containerClasses = `soccer-overlay-container ${visible ? 'reveal' : 'hide-overlay'} effect-${animationEffect}`;

    const cssVariablesStyle = {
        '--title-color': titleColor || '#ffffff',
        '--thanks-color': thanksColor || '#f9e596',
        '--particles-color': particlesColor || '#d4af37',
        '--num-glow': numGlow,
        '--num-glow-strong': numGlowStrong,
        '--bg-gradient-start': backgroundGradientStart || '#0c0c12',
        '--bg-gradient-end': backgroundGradientEnd || '#050508',
        '--num-grad-start': numberGradientStart || '#ffffff',
        '--num-grad-mid': numberGradientMiddle || '#d4af37',
        '--num-grad-end': numberGradientEnd || '#8a631b',
        '--overlay-scale': scale || 1.0,
        '--title-size': `${titleSize || 0.9}vw`,
        '--number-size': `${numberSize || 14.0}vw`,
        '--thanks-size': `${thanksSize || 2.2}vw`,
        '--offset-x': `${numericOffsetX}px`,
        '--offset-y': `${numericOffsetY}vh`,
        '--logo-scale': logoScale || 1.0,
        '--logo-offset-x': `${logoOffsetX || 0}px`,
        '--logo-offset-y': `${logoOffsetY || 0}px`
    };

    const centerYOffset = 50 + numericOffsetY;
    const centerXOffset = 50 + numericOffsetX;

    return (
        <div className={containerClasses} style={cssVariablesStyle}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;800&family=Montserrat:wght@200;300;400;600&display=swap');

                .soccer-overlay-container {
                    position: absolute;
                    top: 0; left: 0;
                    width: 100%; height: 100%;
                    overflow: hidden;
                    font-family: 'Montserrat', sans-serif;
                    background: radial-gradient(circle, var(--bg-gradient-start) 0%, var(--bg-gradient-end) 100%);
                    color: #ffffff;
                    z-index: 10;
                    pointer-events: none;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    transition: opacity 1s ease-in-out;
                }

                .soccer-overlay-container::before {
                    content: "";
                    position: absolute;
                    top: 0; left: 0; width: 100%; height: 100%;
                    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
                    opacity: 0.04;
                    pointer-events: none;
                    z-index: 100;
                }

                .soccer-overlay-container::after {
                    content: "";
                    position: absolute;
                    top: 20%; left: 50%;
                    transform: translateX(-50%);
                    width: 80vw; height: 60vh;
                    background: radial-gradient(ellipse at center, rgba(212, 175, 55, 0.08) 0%, transparent 60%);
                    pointer-events: none;
                    z-index: 0;
                    animation: cb-breathe 8s ease-in-out infinite alternate;
                }

                @keyframes cb-breathe {
                    0% { opacity: 0.4; transform: translateX(-50%) scale(0.9); }
                    100% { opacity: 1; transform: translateX(-50%) scale(1.1); }
                }

                /* DESTELLO SOLAR DORADO EXPANSIVO CENTRAL (Detona en disperse) */
                .soccer-gold-burst {
                    position: absolute;
                    top: calc(50% + var(--offset-y));
                    left: calc(50% + var(--offset-x));
                    transform: translate(-50%, -50%) scale(0.1);
                    width: 45vw; height: 45vw;
                    background: radial-gradient(circle, rgba(255, 235, 170, 0.95) 0%, rgba(212, 175, 55, 0.6) 30%, rgba(138, 99, 27, 0.2) 65%, transparent 80%);
                    filter: blur(35px);
                    mix-blend-mode: screen;
                    opacity: 0;
                    z-index: 10;
                    pointer-events: none;
                    animation: gold-burst-expand 2.2s cubic-bezier(0.1, 0.8, 0.2, 1) forwards;
                    will-change: transform, opacity;
                }

                @keyframes gold-burst-expand {
                    0% { transform: translate(-50%, -50%) scale(0.1); opacity: 0; }
                    15% { opacity: 0.98; }
                    80% { opacity: 0.95; }
                    100% { transform: translate(-50%, -50%) scale(2.8); opacity: 0; }
                }

                .soccer-gold-dust {
                    position: absolute;
                    background-color: var(--particles-color);
                    border-radius: 50%;
                    box-shadow: 0 0 15px var(--particles-color), 0 0 30px #ffffff;
                    opacity: 0;
                    pointer-events: none;
                    z-index: 2;
                    animation: float-dust-up linear infinite;
                }

                @keyframes float-dust-up {
                    0% { transform: translateY(0) scale(0.5); opacity: 0; }
                    20% { opacity: 0.6; }
                    80% { opacity: 0.6; }
                    100% { transform: translateY(-70vh) scale(1.4); opacity: 0; }
                }

                .soccer-overlay-container.hide-overlay {
                    opacity: 0;
                }

                .soccer-text-ui {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    transform: translate(var(--offset-x), var(--offset-y)) scale(var(--overlay-scale));
                    transform-origin: center center;
                    will-change: transform;
                }

                .soccer-subtitle {
                    font-size: var(--title-size);
                    font-weight: 200;
                    letter-spacing: 0.6vw;
                    text-transform: uppercase;
                    color: var(--title-color);
                    margin-bottom: 2vh;
                    text-shadow: 0 0 15px rgba(255, 255, 255, 0.3);
                    opacity: 0;
                    will-change: transform, opacity;
                }

                /* Difuminado suave desde el inicio de la animación */
                .reveal .soccer-subtitle {
                    animation: cb-reveal-sub 2s cubic-bezier(0.25, 1, 0.5, 1) forwards 0.8s;
                }

                /* MONOLITO (NÚMERO METÁLICO) CON DESTELLO DE LUZ DIAGONAL BARRIDO */
                .soccer-monolith {
                    font-family: 'Cinzel', serif;
                    font-size: var(--number-size);
                    font-weight: 600;
                    line-height: 1;
                    text-align: center;
                    background: linear-gradient(
                        120deg, 
                        var(--num-grad-start) 0%, 
                        var(--num-grad-mid) 20%, 
                        #ffffff 30%, 
                        #ffffff 40%, 
                        var(--num-grad-mid) 50%, 
                        var(--num-grad-end) 80%, 
                        var(--num-grad-start) 100%
                    );
                    background-size: 200% auto;
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    opacity: 0;
                    will-change: transform, opacity, filter;
                }

                .soccer-monolith.monolith-reveal {
                    animation: cb-reveal-num 1.6s cubic-bezier(0.165, 0.84, 0.44, 1) forwards,
                               gold-shine-sweep 4.5s ease-in-out infinite;
                }

                @keyframes gold-shine-sweep {
                    0% { background-position: -200% center; }
                    25% { background-position: 200% center; }
                    100% { background-position: 200% center; }
                }

                .soccer-thanks-wrapper {
                    height: 1.5vw;
                    margin-top: 2vh;
                    display: flex;
                    justify-content: center;
                    align-items: flex-start;
                }

                .soccer-thanks {
                    font-size: var(--thanks-size);
                    font-weight: 300;
                    letter-spacing: 1.6vw;
                    margin-right: -1.6vw;
                    text-transform: uppercase;
                    color: var(--thanks-color);
                    opacity: 0;
                    text-shadow: 0 0 25px var(--num-glow-strong);
                    will-change: transform, opacity;
                }

                .reveal .soccer-thanks {
                    animation: cb-reveal-thanks 2s cubic-bezier(0.2, 0.8, 0.2, 1) forwards 4.9s;
                }

                @keyframes cb-reveal-sponsor-pedestal {
                    0% { 
                        opacity: 0; 
                        transform: translate(var(--logo-offset-x), calc(var(--logo-offset-y) + 20px)) scale(var(--logo-scale)); 
                    }
                    100% { 
                        opacity: 1; 
                        transform: translate(var(--logo-offset-x), var(--logo-offset-y)) scale(var(--logo-scale)); 
                    }
                }
                @keyframes cb-reveal-num {
                    0% { opacity: 0; transform: scale(1.15); filter: blur(10px); }
                    100% { opacity: 1; transform: scale(1); filter: blur(0px) drop-shadow(0 0 30px var(--num-glow)); }
                }
                @keyframes cb-reveal-thanks {
                    0% { opacity: 0; transform: translateY(-20px); }
                    100% { opacity: 1; transform: translateY(0); }
                }

                /* ESTILO PARA LAS BOLITAS DE INTRODUCCIÓN */
                .soccer-intro-particle {
                    position: absolute;
                    border-radius: 50%;
                    transform: translate(-50%, -50%);
                    pointer-events: none;
                    z-index: 100;
                    will-change: left, top, opacity, transform;
                }

                /* Animación de parpadeo y flotación estelar (Fase 1) */
                .soccer-intro-particle.particle-twinkling {
                    animation: gold-twinkle-float var(--twinkle-dur) ease-in-out infinite;
                }

                @keyframes gold-twinkle-float {
                    0% { transform: translate(-50%, -50%) translate(0, 0) scale(1); opacity: 0.4; }
                    50% { transform: translate(-50%, -50%) translate(var(--twinkle-x), var(--twinkle-y)) scale(1.2); opacity: 0.95; }
                    100% { transform: translate(-50%, -50%) translate(0, 0) scale(1); opacity: 0.4; }
                }

                /* PATROCINIO ULTRA PREMIUM CON DESTELLO DE LUZ DIAGONAL */
                .soccer-sponsor-wrapper {
                    position: absolute;
                    bottom: 7vh;
                    left: 50%;
                    transform: translateX(-50%);
                    z-index: 5;
                    pointer-events: none;
                }

                .soccer-sponsor-pedestal {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    transform: translate(var(--logo-offset-x), var(--logo-offset-y)) scale(var(--logo-scale));
                    transform-origin: bottom center;
                    opacity: 0;
                    will-change: transform, opacity;
                }

                .reveal .soccer-sponsor-pedestal {
                    animation: cb-reveal-sponsor-pedestal 2s cubic-bezier(0.2, 0.8, 0.2, 1) forwards 4.8s;
                }

                .soccer-sponsor-brand {
                    position: relative;
                    padding: 2vh 4vw;
                    background: linear-gradient(180deg, rgba(20,20,20,0.8) 0%, rgba(0,0,0,0.4) 100%);
                    backdrop-filter: blur(15px);
                    -webkit-backdrop-filter: blur(15px);
                    border-radius: 2px;
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    box-shadow: 0 20px 40px rgba(0,0,0,0.5), inset 0 0 20px rgba(212, 175, 55, 0.05);
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                }

                .soccer-logo-img {
                    height: 12vh;
                    max-width: 50vw;
                    object-fit: contain;
                    display: block;
                    margin: 0 auto;
                    filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.15));
                }

                .soccer-sponsor-brand.with-sweep::after {
                    content: '';
                    position: absolute;
                    top: 0; left: -150%; width: 100%; height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent);
                    transform: skewX(-25deg);
                    animation: glass-sweep-effect 4.5s infinite ease-in-out;
                }

                @keyframes glass-sweep-effect {
                    0% { left: -150%; }
                    25% { left: 150%; }
                    100% { left: 150%; }
                }
            `}</style>

            {/* Partículas de polvo dorado continuas de fondo */}
            {visible && phase === 'done' && goldenParticles.map(p => (
                <div 
                    key={p.id} 
                    className="soccer-gold-dust" 
                    style={{
                        left: p.left,
                        bottom: p.bottom,
                        width: p.size,
                        height: p.size,
                        animationDelay: p.delay,
                        animationDuration: p.duration
                    }}
                />
            ))}

            {/* Destello solar dorado expansivo (Detonado exclusivamente en disperse) */}
            {visible && phase === 'disperse' && (
                <div className="soccer-gold-burst" />
            )}

            {/* SISTEMA DE ANIMACIÓN DE INTRODUCCIÓN DE PARTÍCULAS (PANTALLA COMPLETA - ALINEACIÓN DE PRECISIÓN) */}
            {visible && phase !== 'done' && (() => {
                return introParticles.map(p => {
                    let left = `${p.scatterLeft}%`;
                    let top = `${p.scatterTop}%`;
                    let opacity = 0.5;
                    let scale = 1;
                    
                    if (phase === 'form') {
                        if (targets.length > 0) {
                            const target = targets[p.id % targets.length];
                            const jitterX = (Math.random() - 0.5) * 0.12;
                            const jitterY = (Math.random() - 0.5) * 0.12;
                            left = `${target.x + jitterX}%`;
                            top = `${target.y + jitterY}%`;
                            opacity = 1.0;
                        } else {
                            left = `${centerXOffset}%`;
                            top = `${centerYOffset}%`;
                        }
                    } else if (phase === 'disperse') {
                        // Fase de difuminado hacia arriba (evaporación vertical, Imagen 4 y 5)
                        if (targets.length > 0) {
                            const target = targets[p.id % targets.length];
                            const floatUpDistance = Math.random() * 22 + 15; // sube entre 15% y 37% de la pantalla hacia el cielo
                            const jitterX = (Math.random() - 0.5) * 3.5;
                            left = `${target.x + jitterX}%`;
                            top = `${target.y - floatUpDistance}%`;
                            opacity = 0.0;
                            scale = 0.15;
                        } else {
                            left = `${centerXOffset}%`;
                            top = `${centerYOffset - 25}%`;
                            opacity = 0.0;
                        }
                    }
                    
                    const isTwinkling = phase === 'scatter' ? 'particle-twinkling' : '';
                    
                    return (
                        <div 
                            key={p.id}
                            className={`soccer-intro-particle ${isTwinkling}`}
                            style={{
                                left: left,
                                top: top,
                                width: `${p.size}px`,
                                height: `${p.size}px`,
                                backgroundColor: p.color,
                                boxShadow: `0 0 8px ${p.color}, 0 0 16px ${p.color}`,
                                transition: phase === 'scatter' 
                                    ? 'none' 
                                    : `left 0.9s cubic-bezier(0.25, 1, 0.3, 1) ${p.delay}s, top 0.9s cubic-bezier(0.25, 1, 0.3, 1) ${p.delay}s, opacity 0.8s ease-out, transform 0.9s ease-out`,
                                transform: `translate(-50%, -50%) scale(${scale})`,
                                '--twinkle-x': p.twinkleX,
                                '--twinkle-y': p.twinkleY,
                                '--twinkle-dur': p.twinkleDuration
                            }}
                        />
                    );
                });
            })()}

            {/* UI de Textos Definitiva */}
            <div className="soccer-text-ui">
                <div className="soccer-subtitle">{titleText}</div>
                <div className={`soccer-monolith ${(phase === 'disperse' || phase === 'done') ? 'monolith-reveal' : ''}`}>
                    {numberText}
                </div>
                <div className="soccer-thanks-wrapper">
                    <div className="soccer-thanks">{thanksText}</div>
                </div>
            </div>

            {/* Sección de Logotipo Patrocinador */}
            <div className="soccer-sponsor-wrapper">
                <div className="soccer-sponsor-pedestal">
                    <div className={`soccer-sponsor-brand ${showLogoSweep ? 'with-sweep' : ''}`}>
                        <img 
                            src={logoDataUrl || "/img/overlays/soccer/logo.png"} 
                            alt="Logo Patrocinador" 
                            className="soccer-logo-img" 
                            style={{ 
                                transform: `scale(${logoScale || 1.0})`
                            }} 
                        />
                        <p style={{ 
                            textAlign: 'center', 
                            color: '#ffffff', 
                            opacity: 0.8, 
                            fontWeight: 300, 
                            letterSpacing: '1px',
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: '0.9vw',
                            margin: '1.5vh 0 0 0'
                        }}>
                            Hecho con ♥ por Interaktia
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { Soccer };
export default Soccer;
